/**
 * Nuvra Workflow Orchestration Engine
 *
 * Persistence: Supabase workflow_definitions + workflow_executions tables.
 * In-memory cache is populated on init and kept in sync via realtime subscription.
 *
 * executeWorkflow() records a "running" execution in Supabase and returns immediately.
 * Status transitions require a real server-side executor - no simulated completion.
 */

import { supabase } from "@/integrations/supabase/client";
import type { RealtimeChannel } from "@supabase/supabase-js";
import type { Json } from "@/integrations/supabase/types";
import { aiRouter } from "@/ai/providers/aiRouter";

// ─── Types ───────────────────────────────────────────────────────────

export type TriggerType  = "database_event" | "webhook" | "auth_event" | "schedule" | "external_service" | "manual";
export type ConditionOp  = "equals" | "not_equals" | "gt" | "lt" | "contains" | "regex" | "is_true" | "is_false";
export type ActionType   = "db_query" | "api_call" | "send_email" | "notification" | "payment" | "deploy" | "custom_code" | "ai_agent" | "sub_workflow" | "human_approval" | "delay";
export type NodeKind     = "trigger" | "condition" | "action" | "parallel" | "loop" | "wait" | "approval";
export type ExecStatus   = "pending" | "running" | "completed" | "failed" | "retrying" | "waiting_approval" | "cancelled" | "paused";
export type WorkflowEnv  = "development" | "staging" | "production";

export interface Position { x: number; y: number; }

export interface WorkflowNode {
  id: string; kind: NodeKind; label: string; config: Record<string, unknown>;
  position: Position; triggerType?: TriggerType; actionType?: ActionType;
  conditionOp?: ConditionOp; retryPolicy?: RetryPolicy;
}

export interface WorkflowEdge {
  id: string; source: string; target: string; label?: string; condition?: string;
}

export interface RetryPolicy {
  maxAttempts: number; backoffMs: number; backoffMultiplier: number;
}

export interface WorkflowDefinition {
  id: string; name: string; description: string;
  nodes: WorkflowNode[]; edges: WorkflowEdge[];
  version: number; env: WorkflowEnv;
  status: "draft" | "active" | "paused" | "archived";
  createdAt: number; updatedAt: number;
  tags: string[]; secretRefs: string[];
}

export interface StepResult {
  nodeId: string; status: ExecStatus;
  startedAt: number; completedAt?: number;
  output?: unknown; error?: string; attempt: number;
}

export interface WorkflowExecution {
  id: string; workflowId: string; workflowVersion: number;
  status: ExecStatus; startedAt: number; completedAt?: number;
  triggerData: unknown; steps: StepResult[];
  env: WorkflowEnv; durationMs?: number; cost?: number;
}

export interface WorkflowVersionSnapshot {
  version: number; snapshot: WorkflowDefinition;
  createdAt: number; message: string;
}

export interface WorkflowAnalytics {
  workflowId: string; totalRuns: number; successRate: number;
  avgDurationMs: number; failureRate: number; lastRun?: number;
  topErrors: { message: string; count: number }[];
}

export interface IntegrationConnector {
  id: string; name: string; category: string; icon: string;
  connected: boolean; actions: string[];
}

// ─── In-memory cache ─────────────────────────────────────────────────

let workflows: WorkflowDefinition[]   = [];
let executions: WorkflowExecution[]   = [];
const versionHistory = new Map<string, WorkflowVersionSnapshot[]>();
let stateVersion   = 0;
const listeners    = new Set<() => void>();

function notify() { stateVersion++; listeners.forEach((fn) => fn()); }
export function onWorkflowChange(fn: () => void) { listeners.add(fn); return () => listeners.delete(fn); }
export function getWorkflowVersion() { return stateVersion; }

// ─── ID helpers ──────────────────────────────────────────────────────

function uid() { return `wf-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`; }
function nid() { return `n-${Math.random().toString(36).slice(2, 8)}`; }
function eid() { return `e-${Math.random().toString(36).slice(2, 8)}`; }
function xid() { return `exec-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`; }

// JSON.parse returns `any`, which is assignable to Json without an unsafe cast.
function toJson(v: unknown): Json { return JSON.parse(JSON.stringify(v)); }

// ─── Supabase row ↔ type mapping ────────────────────────────────────

function rowToWorkflow(row: Record<string, unknown>): WorkflowDefinition {
  return {
    id:          row.workflow_key as string,
    name:        row.name as string,
    description: (row.description as string) ?? "",
    nodes:       (row.nodes as WorkflowNode[]) ?? [],
    edges:       (row.edges as WorkflowEdge[]) ?? [],
    version:     row.version as number,
    env:         row.env as WorkflowEnv,
    status:      row.status as WorkflowDefinition["status"],
    tags:        (row.tags as string[]) ?? [],
    secretRefs:  (row.secret_refs as string[]) ?? [],
    createdAt:   new Date(row.created_at as string).getTime(),
    updatedAt:   new Date(row.updated_at as string).getTime(),
  };
}

function workflowToRow(wf: WorkflowDefinition, userId: string) {
  return {
    workflow_key: wf.id, user_id: userId,
    name: wf.name, description: wf.description,
    nodes: toJson(wf.nodes), edges: toJson(wf.edges),
    version: wf.version, env: wf.env, status: wf.status,
    tags: wf.tags, secret_refs: wf.secretRefs,
  };
}

function rowToExecution(row: Record<string, unknown>): WorkflowExecution {
  return {
    id:              row.execution_key as string,
    workflowId:      row.workflow_key as string,
    workflowVersion: row.workflow_version as number,
    status:          row.status as ExecStatus,
    startedAt:       new Date(row.started_at as string).getTime(),
    completedAt:     row.completed_at ? new Date(row.completed_at as string).getTime() : undefined,
    triggerData:     (row.trigger_data as unknown) ?? {},
    steps:           (row.steps as StepResult[]) ?? [],
    env:             row.env as WorkflowEnv,
    durationMs:      (row.duration_ms as number | null) ?? undefined,
    cost:            (row.cost as number | null) ?? undefined,
  };
}

function executionToRow(exec: WorkflowExecution, userId: string | null) {
  return {
    execution_key:    exec.id,
    workflow_key:     exec.workflowId,
    user_id:          userId,
    workflow_version: exec.workflowVersion,
    status:           exec.status,
    env:              exec.env,
    trigger_data:     toJson(exec.triggerData),
    steps:            toJson(exec.steps),
    started_at:       new Date(exec.startedAt).toISOString(),
    completed_at:     exec.completedAt ? new Date(exec.completedAt).toISOString() : null,
    duration_ms:      exec.durationMs ?? null,
    cost:             exec.cost ?? null,
  };
}

// ─── Supabase init + realtime ────────────────────────────────────────

let realtimeChannel: RealtimeChannel | null = null;
let loadedUserId: string | null = null;

async function loadFromSupabase(): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;
  loadedUserId = user.id;

  const [wfRes, execRes] = await Promise.allSettled([
    supabase.from("workflow_definitions")
      .select("*").eq("user_id", user.id).order("updated_at", { ascending: false }),
    supabase.from("workflow_executions")
      .select("*").eq("user_id", user.id).order("started_at", { ascending: false }).limit(200),
  ]);

  if (wfRes.status === "fulfilled" && wfRes.value.data) {
    workflows = wfRes.value.data.map(rowToWorkflow);
  }
  if (execRes.status === "fulfilled" && execRes.value.data) {
    executions = execRes.value.data.map(rowToExecution);
  }
  notify();
}

async function refreshFromSupabase(): Promise<void> {
  if (!loadedUserId) return;
  const [wfRes, execRes] = await Promise.allSettled([
    supabase.from("workflow_definitions").select("*").eq("user_id", loadedUserId).order("updated_at", { ascending: false }),
    supabase.from("workflow_executions").select("*").eq("user_id", loadedUserId).order("started_at", { ascending: false }).limit(200),
  ]);
  if (wfRes.status === "fulfilled" && wfRes.value.data)   workflows  = wfRes.value.data.map(rowToWorkflow);
  if (execRes.status === "fulfilled" && execRes.value.data) executions = execRes.value.data.map(rowToExecution);
  notify();
}

export function initWorkflowService(): () => void {
  loadFromSupabase().catch(console.warn);

  realtimeChannel?.unsubscribe();
  realtimeChannel = supabase
    .channel("nuvra-workflows")
    .on("postgres_changes", { event: "*", schema: "public", table: "workflow_definitions" },  () => refreshFromSupabase())
    .on("postgres_changes", { event: "*", schema: "public", table: "workflow_executions" },   () => refreshFromSupabase())
    .subscribe();

  return () => { realtimeChannel?.unsubscribe(); realtimeChannel = null; };
}

// ─── CRUD ────────────────────────────────────────────────────────────

export function getWorkflows(): WorkflowDefinition[] { return [...workflows]; }
export function getWorkflow(id: string): WorkflowDefinition | undefined { return workflows.find((w) => w.id === id); }

export function createWorkflow(name: string, description: string): WorkflowDefinition {
  const wf: WorkflowDefinition = {
    id: uid(), name, description, nodes: [], edges: [], version: 1,
    env: "development", status: "draft",
    createdAt: Date.now(), updatedAt: Date.now(), tags: [], secretRefs: [],
  };
  workflows = [...workflows, wf];
  notify();

  supabase.auth.getUser().then(({ data: { user } }) => {
    if (!user) return;
    supabase.from("workflow_definitions").insert(workflowToRow(wf, user.id))
      .then(({ error }) => { if (error) console.warn("[workflows] create failed:", error.message); });
  });

  return wf;
}

export function updateWorkflow(id: string, patch: Partial<WorkflowDefinition>) {
  const updated = { ...patch, updatedAt: Date.now() };
  workflows = workflows.map((w) => w.id === id ? { ...w, ...updated } : w);
  notify();

  supabase.from("workflow_definitions")
    .update({
      name:        patch.name,
      description: patch.description,
      nodes:       patch.nodes !== undefined ? toJson(patch.nodes) : undefined,
      edges:       patch.edges !== undefined ? toJson(patch.edges) : undefined,
      status:      patch.status,
      env:         patch.env,
      tags:        patch.tags,
      secret_refs: patch.secretRefs,
    })
    .eq("workflow_key", id)
    .then(({ error }) => { if (error) console.warn("[workflows] update failed:", error.message); });
}

export function deleteWorkflow(id: string) {
  workflows = workflows.filter((w) => w.id !== id);
  notify();
  supabase.from("workflow_definitions").delete().eq("workflow_key", id)
    .then(({ error }) => { if (error) console.warn("[workflows] delete failed:", error.message); });
}

export function addNode(workflowId: string, node: WorkflowNode) {
  workflows = workflows.map((w) => w.id === workflowId
    ? { ...w, nodes: [...w.nodes, node], updatedAt: Date.now() } : w);
  notify();
  const wf = workflows.find((w) => w.id === workflowId);
  if (wf) {
    supabase.from("workflow_definitions").update({ nodes: toJson(wf.nodes) }).eq("workflow_key", workflowId)
      .then(({ error }) => { if (error) console.warn("[workflows] addNode failed:", error.message); });
  }
}

export function removeNode(workflowId: string, nodeId: string) {
  workflows = workflows.map((w) => w.id === workflowId ? {
    ...w,
    nodes: w.nodes.filter((n) => n.id !== nodeId),
    edges: w.edges.filter((e) => e.source !== nodeId && e.target !== nodeId),
    updatedAt: Date.now(),
  } : w);
  notify();
  const wf = workflows.find((w) => w.id === workflowId);
  if (wf) {
    supabase.from("workflow_definitions").update({ nodes: toJson(wf.nodes), edges: toJson(wf.edges) }).eq("workflow_key", workflowId)
      .then(({ error }) => { if (error) console.warn("[workflows] removeNode failed:", error.message); });
  }
}

export function addEdge(workflowId: string, edge: WorkflowEdge) {
  workflows = workflows.map((w) => w.id === workflowId
    ? { ...w, edges: [...w.edges, edge], updatedAt: Date.now() } : w);
  notify();
  const wf = workflows.find((w) => w.id === workflowId);
  if (wf) {
    supabase.from("workflow_definitions").update({ edges: toJson(wf.edges) }).eq("workflow_key", workflowId)
      .then(({ error }) => { if (error) console.warn("[workflows] addEdge failed:", error.message); });
  }
}

// ─── Execution ───────────────────────────────────────────────────────

export function getExecutions(workflowId?: string): WorkflowExecution[] {
  const list = workflowId ? executions.filter((e) => e.workflowId === workflowId) : executions;
  return [...list].sort((a, b) => b.startedAt - a.startedAt);
}

export function executeWorkflow(workflowId: string, triggerData: unknown = {}): WorkflowExecution {
  const wf = workflows.find((w) => w.id === workflowId);
  if (!wf) throw new Error("Workflow not found");

  const exec: WorkflowExecution = {
    id: xid(), workflowId, workflowVersion: wf.version,
    status: "running", startedAt: Date.now(),
    triggerData, steps: [], env: wf.env,
  };
  executions = [exec, ...executions];
  notify();

  // Persist execution record - stays "running" until a real executor updates it
  supabase.auth.getUser().then(({ data: { user } }) => {
    supabase.from("workflow_executions")
      .insert(executionToRow(exec, user?.id ?? null))
      .then(({ error }) => { if (error) console.warn("[workflows] execution persist failed:", error.message); });
  });

  return exec;
}

// ─── Versioning ──────────────────────────────────────────────────────

export function saveVersion(workflowId: string, message: string) {
  const wf = workflows.find((w) => w.id === workflowId);
  if (!wf) return;
  const hist = versionHistory.get(workflowId) ?? [];
  hist.push({ version: wf.version, snapshot: { ...wf }, createdAt: Date.now(), message });
  versionHistory.set(workflowId, hist);
  workflows = workflows.map((w) => w.id === workflowId
    ? { ...w, version: w.version + 1, updatedAt: Date.now() } : w);
  notify();

  const next = workflows.find((w) => w.id === workflowId);
  if (next) {
    supabase.from("workflow_definitions").update({ version: next.version }).eq("workflow_key", workflowId)
      .then(({ error }) => { if (error) console.warn("[workflows] saveVersion failed:", error.message); });
  }
}

export function getVersionHistory(workflowId: string): WorkflowVersionSnapshot[] {
  return versionHistory.get(workflowId) ?? [];
}

export function restoreVersion(workflowId: string, version: number) {
  const hist = versionHistory.get(workflowId);
  const snap = hist?.find((v) => v.version === version);
  if (!snap) return;
  const restored = { ...snap.snapshot, version: (workflows.find((w) => w.id === workflowId)?.version ?? 0) + 1, updatedAt: Date.now() };
  workflows = workflows.map((w) => w.id === workflowId ? restored : w);
  notify();

  supabase.auth.getUser().then(({ data: { user } }) => {
    if (!user) return;
    supabase.from("workflow_definitions").upsert(workflowToRow(restored, user.id), { onConflict: "workflow_key" })
      .then(({ error }) => { if (error) console.warn("[workflows] restoreVersion failed:", error.message); });
  });
}

// ─── Analytics ───────────────────────────────────────────────────────

export function getWorkflowAnalytics(workflowId: string): WorkflowAnalytics {
  const runs      = executions.filter((e) => e.workflowId === workflowId);
  const completed = runs.filter((r) => r.status === "completed");
  const failed    = runs.filter((r) => r.status === "failed");
  const durations = runs.filter((r) => r.durationMs).map((r) => r.durationMs!);
  const errorMap: Record<string, number> = {};
  failed.forEach((f) => {
    const err = f.steps.find((s) => s.error)?.error || "Unknown";
    errorMap[err] = (errorMap[err] || 0) + 1;
  });
  return {
    workflowId, totalRuns: runs.length,
    successRate:   runs.length ? Math.round((completed.length / runs.length) * 100) : 0,
    avgDurationMs: durations.length ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length) : 0,
    failureRate:   runs.length ? Math.round((failed.length / runs.length) * 100) : 0,
    lastRun:       runs[0]?.startedAt,
    topErrors: Object.entries(errorMap).map(([message, count]) => ({ message, count }))
      .sort((a, b) => b.count - a.count).slice(0, 5),
  };
}

// ─── Integration Connectors ──────────────────────────────────────────
// Static registry - connector status driven by backend config, not hardcoded booleans.
// "connected" here reflects whether the platform ships with native support, not runtime state.

export function getIntegrationConnectors(): IntegrationConnector[] {
  return [
    { id: "anthropic", name: "Anthropic",     category: "AI",            icon: "🧠", connected: true,  actions: ["Generate", "Embed", "Stream", "Tools"] },
    { id: "supabase",  name: "Supabase",       category: "Database",      icon: "⚡", connected: true,  actions: ["Query", "Insert", "Update", "Delete", "RPC", "Realtime"] },
    { id: "stripe",    name: "Stripe",          category: "Payments",      icon: "💳", connected: false, actions: ["Charge", "Refund", "Subscribe"] },
    { id: "slack",     name: "Slack",           category: "Communication", icon: "💬", connected: false, actions: ["Send Message", "Create Channel"] },
    { id: "sendgrid",  name: "SendGrid",        category: "Email",         icon: "📧", connected: false, actions: ["Send Email", "Add Contact"] },
    { id: "twilio",    name: "Twilio",          category: "Communication", icon: "📱", connected: false, actions: ["Send SMS", "Make Call"] },
    { id: "github",    name: "GitHub",          category: "DevOps",        icon: "🐙", connected: false, actions: ["Create Issue", "Merge PR", "Deploy"] },
    { id: "openai",    name: "OpenAI",          category: "AI",            icon: "🤖", connected: false, actions: ["Generate Text", "Embed", "Moderate"] },
  ];
}

// ─── AI Generation ───────────────────────────────────────────────────

export async function generateWorkflowFromPrompt(prompt: string): Promise<WorkflowDefinition> {
  const result = await aiRouter.generate({
    prompt: `Generate a workflow automation for: "${prompt}"\\n\\nReturn ONLY a JSON array of 3-6 workflow nodes:\\n[{"kind":"trigger|condition|action","label":"string","triggerType":"database_event|webhook|schedule|manual","conditionOp":"is_true|is_false|equals","actionType":"send_email|api_call|db_query|notification|custom_code|payment|ai_agent"}]\\n\\nRules: First node must be kind "trigger". Use condition nodes for branching. End with action nodes.`,
    projectId: "workflow-generation",
    systemPrompt: "You are a workflow automation designer. Return ONLY a valid JSON array of node objects. No markdown, no explanation.",
    maxTokens: 512,
  });

  if (result.error || !result.raw) {
    throw new Error(
      `AI_WORKFLOW_GENERATION_FAILED: ${result.error?.message ?? "The AI provider returned no usable workflow output."}`,
    );
  }

  const match = result.raw.trim().match(/\[[\s\S]*\]/);
  if (!match) {
    throw new Error("AI_WORKFLOW_GENERATION_INVALID_OUTPUT: The AI provider did not return a JSON workflow array.");
  }

  let parsed: Array<Record<string, unknown>>;
  try {
    parsed = JSON.parse(match[0]) as Array<Record<string, unknown>>;
  } catch {
    throw new Error("AI_WORKFLOW_GENERATION_INVALID_OUTPUT: The AI provider returned invalid JSON.");
  }

  if (!Array.isArray(parsed) || parsed.length === 0 || parsed[0]?.kind !== "trigger") {
    throw new Error("AI_WORKFLOW_GENERATION_INVALID_OUTPUT: Workflow must begin with a trigger.");
  }

  const nodes: WorkflowNode[] = parsed.map((n, i) => ({
    id: nid(),
    kind: (n.kind as WorkflowNode["kind"]) ?? "action",
    label: typeof n.label === "string" ? n.label : `Step ${i + 1}`,
    config: {},
    position: { x: 250, y: 40 + i * 120 },
    ...(n.triggerType ? { triggerType: n.triggerType as TriggerType } : {}),
    ...(n.conditionOp ? { conditionOp: n.conditionOp as ConditionOp } : {}),
    ...(n.actionType ? { actionType: n.actionType as ActionType } : {}),
  }));

  const edges = nodes.slice(0, -1).map((n, i) => ({ id: eid(), source: n.id, target: nodes[i + 1].id }));

  const wf: WorkflowDefinition = {
    id: uid(),
    name: prompt.slice(0, 40),
    description: `Generated: ${prompt}`,
    nodes,
    edges,
    version: 1,
    env: "development",
    status: "draft",
    createdAt: Date.now(),
    updatedAt: Date.now(),
    tags: ["ai-generated"],
    secretRefs: [],
  };
  workflows = [...workflows, wf];
  notify();

  supabase.auth.getUser().then(({ data: { user } }) => {
    if (!user) return;
    supabase.from("workflow_definitions").insert(workflowToRow(wf, user.id))
      .then(({ error }) => { if (error) console.warn("[workflows] generate persist failed:", error.message); });
  });

  return wf;
}
